'use client';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { ArrowUpIcon } from 'lucide-react';
import { cn } from '@util/index';
import { CloseIcon } from '@c/icons';
import SafeImage from '@ui/SafeImage';
import { SupportBotButton } from 'features/support-bot/components/SupportBotButton';

export interface ChatInputProps {
  placeholder?: string;
  message: string;
  setMessage: (message: string) => void;
  onSend: () => void;
  onAttachment?: () => void;
  disabled?: boolean;
  className?: string;
  fileList?: File[];
  onFileListChange?: (files: File[]) => void;
  convertingImages?: boolean;
  onImageClick?: (index: number) => void;
  chatId: string;
  onDropFiles?: (files: File[]) => void;
  isSupport?: boolean;
}

const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      placeholder = 'Write your message here...',
      message,
      setMessage,
      onSend,
      onAttachment,
      disabled = false,
      className,
      fileList = [],
      onFileListChange,
      convertingImages = false,
      onImageClick,
      chatId,
      onDropFiles,
      isSupport = false,
    },
    ref
  ) => {
    const dragCounter = useRef<number>(0);
    const [isSingleLine, setIsSingleLine] = useState<boolean>(true);
    const [isDragOver, setIsDragOver] = useState<boolean>(false);
    const TEXTAREA_HEIGHT = '4.5rem';

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(e.target.value);

        // Auto-resize textarea
        e.target.style.height = TEXTAREA_HEIGHT;
        e.target.style.height = `${Math.max(e.target.scrollHeight + 10, 50)}px`;

        // Check if content is single line by comparing scroll height to base height
        // Base height is approximately 72px (4.5rem), scrollHeight > 72 indicates wrapping
        const isMultiLine = e.target.scrollHeight > 72;
        setIsSingleLine(!isMultiLine);
      },
      [setMessage]
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter') return;
        if (e.shiftKey) return;

        e.preventDefault();
        if (!disabled && (message.trim() || fileList.length > 0)) {
          onSend();
        }
      },
      [onSend, disabled, message, fileList]
    );

    const handleSendClick = useCallback(() => {
      if (!disabled && (message.trim() || fileList.length > 0)) {
        onSend();
      }
    }, [onSend, disabled, message, fileList]);

    const handleDragEnter = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;
      setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current--;
      if (dragCounter.current === 0) {
        setIsDragOver(false);
      }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current = 0;
        setIsDragOver(false);

        const files = Array.from(e.dataTransfer.files);
        const imageFiles = files.filter((file) =>
          file.type.startsWith('image/')
        );

        if (imageFiles.length > 0 && onDropFiles) {
          onDropFiles(imageFiles);
        }
      },
      [onDropFiles]
    );

    const isActive = message.trim().length > 0 || fileList.length > 0;

    return (
      <div
        className={cn(
          'relative z-10 mx-auto w-full px-4 py-4 sm:w-[calc(100%-6rem)] sm:px-0 sm:py-8 md:w-[calc(100%-8rem)] lg:w-[calc(100%-10rem)]',
          className
        )}
      >
        {fileList.length > 0 && (
          <div className="relative mb-4">
            <div className="grid grid-cols-7 gap-4 sm:flex">
              {fileList.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="relative h-[6rem] w-[6rem] rounded-[2rem] border border-zinc-400"
                >
                  <SafeImage
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    height={64}
                    width={64}
                    className="h-[6rem] min-h-[6rem] w-[6rem] min-w-[6rem] cursor-pointer rounded-[1rem] object-cover transition-opacity hover:opacity-80"
                    onClick={() => onImageClick?.(index)}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-2 z-[100] flex h-[2.4rem] w-[2.4rem] items-center justify-center rounded-full bg-zinc-800 p-2"
                    onClick={() => {
                      if (onFileListChange) {
                        const newFileList = fileList.filter(
                          (_: File, i: number) => i !== index
                        );
                        onFileListChange(newFileList);
                      }
                    }}
                    aria-label="Remove image"
                  >
                    <CloseIcon className="h-full w-full text-white" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {convertingImages && (
          <div className="my-4 flex h-[6rem] w-[6rem] animate-pulse items-center gap-[1.6rem] rounded-brand border-[1.5px] border-black bg-slate-500 px-6 transition-[border-radius] duration-500" />
        )}

        <div
          className={cn(
            'flex items-center gap-4 border px-4 transition-all duration-200',
            isDragOver
              ? 'border-2 border-blue-400 bg-blue-50'
              : 'border-zinc-300 bg-[#F6F6F6]',
            (ref as React.RefObject<HTMLTextAreaElement>)?.current
              ?.scrollHeight &&
              (ref as React.RefObject<HTMLTextAreaElement>)?.current &&
              (ref as React.RefObject<HTMLTextAreaElement>).current &&
              ((ref as React.RefObject<HTMLTextAreaElement>).current
                ?.scrollHeight || 0) > 50
              ? 'rounded-[2rem]'
              : 'rounded-full'
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            minHeight: '6rem',
            height: (ref as React.RefObject<HTMLTextAreaElement>)?.current
              ?.scrollHeight
              ? `${Math.max(
                  ((ref as React.RefObject<HTMLTextAreaElement>).current
                    ?.scrollHeight || 0) + 10,
                  60
                )}px`
              : '6rem',
            maxHeight: 'none',
          }}
        >
          {onAttachment && (
            <button
              type="button"
              onClick={onAttachment}
              className="flex-shrink-0 p-2"
              disabled={disabled}
            >
              <ImagesIcon className="h-12 w-12 text-zinc-600" />
            </button>
          )}

          <textarea
            ref={ref}
            className={cn(
              'flex-1 resize-none overflow-hidden border-none bg-transparent px-4 text-[1.4rem] leading-tight outline-none ring-0 placeholder:text-zinc-500 focus:border-none focus:outline-none focus:ring-0 sm:text-[1.6rem]',
              isSingleLine ? 'py-6' : 'py-4'
            )}
            style={{
              height: TEXTAREA_HEIGHT,
              border: 'none !important',
            }}
            value={message}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
          />

          <div className="flex items-center gap-4">
            <SupportBotButton
              chat_id={chatId}
              onNextMessage={(message) => {
                setMessage(message);
              }}
              currentMessage={message}
            />

            <button
              type="button"
              onClick={handleSendClick}
              className={cn(
                'flex aspect-square h-[4.5rem] w-[4.5rem] flex-shrink-0 items-center justify-center rounded-full border-2 p-4 text-white transition-colors',
                isActive && !disabled
                  ? 'bg-brand-secondary text-white hover:bg-brand-secondary-darker'
                  : 'cursor-not-allowed bg-[#D9D9D9] text-[#7F7F7F]'
              )}
              disabled={disabled || !isActive}
            >
              <ArrowUpIcon className="h-full w-full" />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;

const ImagesIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>Attach image</title>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21,15 16,10 5,21" />
    </svg>
  );
};
